import {
    IonButton,
    IonButtons,
    IonContent,
    IonDatetime,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonPage,
    IonRange,
    IonSelect,
    IonSelectOption,
    IonSkeletonText,
    IonText,
    IonTitle,
    IonToolbar,
    useIonActionSheet
} from '@ionic/react';
import { cameraOutline, copyOutline, createOutline } from 'ionicons/icons';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { format, startOfMonth, subDays, subMonths } from 'date-fns';
import { useRfidProfileMutation, useRfidProfileQuery } from '../query/rfidProfileQuery';
import {
    useRfidProfileAvatarDeleteMutation,
    useRfidProfileAvatarQuery,
    useRfidProfileAvatarUploadMutation
} from '../query/rfidProfileAvatarQuery';
import { useCatActivityStatsQuery, useCatMonthlyActivityStatsQuery } from '../query/activityStatsQuery';
import { useCurrentUserQuery } from '../query/currentUserQuery';
import { useDevicesQuery } from '../query/devicesQuery';
import { useRfidLastSeenQuery } from '../query/rfidLastSeenQuery';
import { monthlyTransitCounts, summarizeCatActivity } from '../utils/activityStatsSummary';
import { RFID_PROFILE_SEX_VALUES, RfidProfileSex } from '../models/RfidProfile';
import { Device } from '../models/Device';
import Timestamp from '../components/Timestamp';
import useUX from '../utils/ux';
import { useCommonTranslation, useComponentTranslation, useModalTranslation, usePageTranslation } from '../hooks/useTranslation';
import StatsSection from '../components/stats/StatsSection';
import { DenyPeekTiles, HourHistogramCard, MonthlyTransitsCard, PreyCard, RecordsList, TransitsCard, WeekdayCard } from '../components/stats/StatCards';
import { formatDay } from '../utils/statsFormat';
import statStyles from '../components/stats/stats.module.css';
import styles from './RfidProfileModal.module.css';

const STATS_WINDOW_DAYS = 30;
// The monthly chart spans this many calendar months, ending with the current
// one. The fetch starts on the 1st of the earliest month so its bar covers a
// whole month instead of however many days a rolling year happens to leave in
// it (worst case 365 days; the gateway caps ranges at 400).
const STATS_MONTHLY_MONTHS = 12;
const MAX_STATS_DEVICE_IDS = 20;
const AVATAR_MAX_DIMENSION = 1024;

/** 'YYYY-MM-DD' from a profile date value that may be a Date or ISO/day string. */
function toDayString(value: Date | string | null | undefined): string | null {
    if (!value) {
        return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
}

function loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const element = new Image();
        element.onload = () => resolve(element);
        element.onerror = () => reject(new Error('Could not read the selected image.'));
        element.src = url;
    });
}

/** Square crop region in natural-image pixels. */
interface CropRect {
    x: number;
    y: number;
    size: number;
}

/** Render the chosen crop square to a JPEG the gateway's 1 MiB avatar limit fits. */
async function renderAvatarCrop(image: HTMLImageElement, crop: CropRect): Promise<Blob> {
    const size = Math.max(1, Math.min(AVATAR_MAX_DIMENSION, Math.round(crop.size)));
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    canvas.getContext('2d')!.drawImage(image, crop.x, crop.y, crop.size, crop.size, 0, 0, size, size);

    return await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not process the selected image.'))), 'image/jpeg', 0.85);
    });
}

const AVATAR_CROP_MAX_ZOOM = 5;

interface Point {
    x: number;
    y: number;
}

// Crop step shown after picking a photo: drag to pan, pinch or use the
// slider to zoom, with a circle marking what the round avatar will show.
const AvatarCropper: React.FC<{
    url: string;
    /** Natural (decoded) image dimensions. */
    width: number;
    height: number;
    onCancel: () => void;
    onConfirm: (crop: CropRect) => void;
}> = ({ url, width, height, onCancel, onConfirm }) => {
    const { t: tCommon } = useCommonTranslation();
    const { t: tModals } = useModalTranslation();

    // zoom 1 = the shorter image side exactly covers the viewport; x/y move
    // the image centre away from the viewport centre, in on-screen pixels.
    const [view, setView] = useState({ zoom: 1, x: 0, y: 0 });
    const pointers = useRef(new Map<number, Point>());

    // The viewport measures 0 while the modal is still presenting, so track
    // its size with an observer rather than a one-shot measurement.
    const viewportRef = useRef<HTMLDivElement>(null);
    const [viewportSize, setViewportSize] = useState(0);
    useLayoutEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport) {
            return;
        }
        const observer = new ResizeObserver(() => setViewportSize(viewport.clientWidth));
        observer.observe(viewport);
        return () => observer.disconnect();
    }, []);

    /** On-screen pixels per natural-image pixel at the given zoom. */
    const scaleFor = (zoom: number) => (viewportSize / Math.min(width, height)) * zoom;

    /** Clamp so the image always covers the whole viewport. */
    const clampView = (zoom: number, x: number, y: number) => {
        const clampedZoom = Math.min(AVATAR_CROP_MAX_ZOOM, Math.max(1, zoom));
        const scale = scaleFor(clampedZoom);
        const maxX = Math.max(0, (width * scale - viewportSize) / 2);
        const maxY = Math.max(0, (height * scale - viewportSize) / 2);
        return {
            zoom: clampedZoom,
            x: Math.min(maxX, Math.max(-maxX, x)),
            y: Math.min(maxY, Math.max(-maxY, y))
        };
    };

    // Drags and pinches are the same move: the image point under the anchor
    // (the finger, or the pinch midpoint) follows it while the zoom changes.
    // Anchors are in viewport coordinates relative to its centre.
    const applyGesture = (anchorBefore: Point, anchorAfter: Point, zoomFactor: number) => {
        setView((view) => {
            const zoom = Math.min(AVATAR_CROP_MAX_ZOOM, Math.max(1, view.zoom * zoomFactor));
            const factor = zoom / view.zoom;
            return clampView(
                zoom,
                anchorAfter.x - factor * (anchorBefore.x - view.x),
                anchorAfter.y - factor * (anchorBefore.y - view.y)
            );
        });
    };

    const onPointerDown = (event: React.PointerEvent) => {
        viewportRef.current?.setPointerCapture(event.pointerId);
        pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    };

    const onPointerMove = (event: React.PointerEvent) => {
        const viewport = viewportRef.current;
        const before = pointers.current.get(event.pointerId);
        if (!viewport || !before || !viewportSize) {
            return;
        }

        const position = { x: event.clientX, y: event.clientY };
        pointers.current.set(event.pointerId, position);

        const rect = viewport.getBoundingClientRect();
        const toLocal = (point: Point): Point => ({
            x: point.x - rect.left - rect.width / 2,
            y: point.y - rect.top - rect.height / 2
        });

        const other = [...pointers.current.entries()].find(([id]) => id !== event.pointerId)?.[1];
        if (other) {
            const distanceBefore = Math.hypot(before.x - other.x, before.y - other.y);
            const distanceAfter = Math.hypot(position.x - other.x, position.y - other.y);
            applyGesture(
                toLocal({ x: (before.x + other.x) / 2, y: (before.y + other.y) / 2 }),
                toLocal({ x: (position.x + other.x) / 2, y: (position.y + other.y) / 2 }),
                distanceBefore > 0 ? distanceAfter / distanceBefore : 1
            );
        } else {
            applyGesture(toLocal(before), toLocal(position), 1);
        }
    };

    const onPointerEnd = (event: React.PointerEvent) => {
        pointers.current.delete(event.pointerId);
    };

    const confirmCrop = () => {
        const scale = scaleFor(view.zoom);
        const size = viewportSize / scale;
        onConfirm({
            x: width / 2 - view.x / scale - size / 2,
            y: height / 2 - view.y / scale - size / 2,
            size
        });
    };

    const scale = viewportSize ? scaleFor(view.zoom) : 0;

    return (
        <div className={styles.cropContainer}>
            <div className={styles.cropTitle}>{tModals('rfid_profile.crop_title')}</div>
            <div
                ref={viewportRef}
                className={styles.cropViewport}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerEnd}
                onPointerCancel={onPointerEnd}
            >
                <img
                    src={url}
                    alt=""
                    draggable={false}
                    className={styles.cropImage}
                    style={{
                        width: width * scale,
                        height: height * scale,
                        transform: `translate(calc(-50% + ${view.x}px), calc(-50% + ${view.y}px))`
                    }}
                />
                <div className={styles.cropMask} aria-hidden />
            </div>
            <div className={`${statStyles.mutedSmall} ${styles.cropHint}`}>{tModals('rfid_profile.crop_hint')}</div>
            <IonRange
                className={styles.cropZoom}
                aria-label={tModals('rfid_profile.zoom')}
                min={1}
                max={AVATAR_CROP_MAX_ZOOM}
                step={0.01}
                value={view.zoom}
                onIonInput={(event) => {
                    const zoom = event.detail.value as number;
                    // Anchored at the viewport centre: offsets scale with the zoom.
                    setView((view) => clampView(zoom, (zoom / view.zoom) * view.x, (zoom / view.zoom) * view.y));
                }}
            />
            <div className={styles.cropActions}>
                <IonButton fill="clear" color="medium" onClick={onCancel}>
                    {tCommon('buttons.cancel')}
                </IonButton>
                <IonButton fill="clear" strong disabled={!viewportSize} onClick={confirmCrop}>
                    {tModals('rfid_profile.use_photo')}
                </IonButton>
            </div>
        </div>
    );
};

const RfidProfileModal = ({ dismiss, rfidCode }: { dismiss: (data?: string | null | undefined | number, role?: string) => void, rfidCode: string }) => {
    const query = useRfidProfileQuery(rfidCode);
    const mutation = useRfidProfileMutation();
    const { presentResult } = useUX();
    const [presentActionSheet] = useIonActionSheet();

    // i18n namespaces: common (buttons), modals (modal-specific), components (field labels), pages (subevent verbs)
    const { t: tCommon } = useCommonTranslation();
    const { t: tModals } = useModalTranslation();
    const { t: tComponents } = useComponentTranslation();
    const { t: tPages } = usePageTranslation();

    const profile = query.data;

    // ion-content measures its fullscreen header offset while the modal is
    // still presenting and records 0; retrigger the measurement once the
    // modal is up so the hero gradient extends beneath the translucent
    // toolbar (which tints the iOS frosting until the hero scrolls away).
    const contentRef = useRef<HTMLIonContentElement>(null);
    useEffect(() => {
        const content = contentRef.current;
        if (!content) {
            return;
        }

        const remeasure = () => {
            content.fullscreen = false;
            content.fullscreen = true;
        };

        const modal = content.closest('ion-modal');
        modal?.addEventListener('ionModalDidPresent', remeasure, { once: true });
        const fallbackTimer = setTimeout(remeasure, 600);

        return () => {
            modal?.removeEventListener('ionModalDidPresent', remeasure);
            clearTimeout(fallbackTimer);
        };
    }, []);

    const [label, setLabel] = useState(profile?.label);
    const [dateOfBirth, setDateOfBirth] = useState<string | null>(toDayString(profile?.dateOfBirth));
    const [sex, setSex] = useState<RfidProfileSex | null>(profile?.sex ?? null);

    useEffect(() => {
        setLabel(profile?.label);
        setDateOfBirth(toDayString(profile?.dateOfBirth));
        setSex(profile?.sex ?? null);
    }, [profile?.label, profile?.dateOfBirth, profile?.sex]);

    // Inline name input sized to its content via a hidden mirror span. The
    // mirror measures 0 while the modal is still presenting (its page isn't
    // laid out yet), so a one-shot measurement would leave the input at the
    // 64px floor; the observer re-fires once layout lands and again if the
    // font swap changes the text metrics.
    const nameMirrorRef = useRef<HTMLSpanElement>(null);
    const nameInputRef = useRef<HTMLInputElement>(null);
    const [nameWidth, setNameWidth] = useState(80);
    useLayoutEffect(() => {
        setNameWidth(nameMirrorRef.current?.offsetWidth ?? 80);
    }, [label]);
    useLayoutEffect(() => {
        const mirror = nameMirrorRef.current;
        if (!mirror) {
            return;
        }
        const observer = new ResizeObserver(() => setNameWidth(mirror.offsetWidth));
        observer.observe(mirror);
        return () => observer.disconnect();
    }, []);

    // Avatar. The merged profile names the household member whose photo is
    // displayed (avatarUserId) - not necessarily the profile row's owner.
    const avatarQuery = useRfidProfileAvatarQuery(profile?.avatarUserId ?? undefined, rfidCode, profile?.avatarUpdatedAt);
    const avatarUploadMutation = useRfidProfileAvatarUploadMutation();
    const avatarDeleteMutation = useRfidProfileAvatarDeleteMutation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const avatarUrl = avatarQuery.data ?? null;

    // Stats across every device the user can see (rollup request cap: 20).
    // Admins query fleet-wide by chip instead, so the profile resolves for
    // cats on devices they do not own (support lookups, shared-event links).
    const devicesQuery = useDevicesQuery();
    const currentUserQuery = useCurrentUserQuery();
    const fleetWideStats = currentUserQuery.data?.userLevel === 'ADMIN';
    const deviceIds = useMemo(
        () => (devicesQuery.data?.pages.flat() ?? []).map((device: Device) => device.deviceId).slice(0, MAX_STATS_DEVICE_IDS),
        [devicesQuery.data]
    );

    // Fetch double the window so the previous 30 days feed the trend line.
    const today = new Date();
    const toDay = format(today, 'yyyy-MM-dd');
    const windowFromDay = format(subDays(today, STATS_WINDOW_DAYS - 1), 'yyyy-MM-dd');
    const fetchFromDay = format(subDays(today, STATS_WINDOW_DAYS * 2 - 1), 'yyyy-MM-dd');
    const monthlyFromDay = format(startOfMonth(subMonths(today, STATS_MONTHLY_MONTHS - 1)), 'yyyy-MM-dd');
    const statsQuery = useCatActivityStatsQuery(rfidCode, deviceIds, fetchFromDay, toDay, { fleetWide: fleetWideStats });
    const monthlyStatsQuery = useCatMonthlyActivityStatsQuery(rfidCode, deviceIds, monthlyFromDay, toDay, { fleetWide: fleetWideStats });

    const stats = useMemo(
        () => (statsQuery.data ? summarizeCatActivity(statsQuery.data.days, windowFromDay, toDay) : null),
        [statsQuery.data, windowFromDay, toDay]
    );

    const monthly = useMemo(
        () => (monthlyStatsQuery.data ? monthlyTransitCounts(monthlyStatsQuery.data.months, toDay) : []),
        [monthlyStatsQuery.data, toDay]
    );

    // Presence: most recent last-seen entry for this chip across devices.
    const lastSeenQuery = useRfidLastSeenQuery();
    const lastSeen = lastSeenQuery.data?.find((item) => item.rfidCode === rfidCode);
    const lastSeenStatus = (() => {
        if (!lastSeen) {
            return null;
        }
        if (lastSeen.locationSource === 'MANUAL' && lastSeen.location) {
            return tComponents(`activity_overview_tile.${lastSeen.location === 'INSIDE' ? 'marked_inside' : 'marked_outside'}`);
        }
        if (lastSeen.lastSubevent) {
            return tPages(`event.timeline.subevents.${lastSeen.lastSubevent.action}_${lastSeen.lastSubevent.direction}`);
        }
        return null;
    })();

    const dirty = (label ?? '') !== (profile?.label ?? '')
        || dateOfBirth !== toDayString(profile?.dateOfBirth)
        || (sex ?? null) !== (profile?.sex ?? null);

    const canSave = dirty && !mutation.isPending;

    const save = async () => {
        presentResult(async () => {
            // Every field is an independent contribution to the merged
            // profile: null clears yours (the household falls back to
            // another member's value), absent leaves it untouched.
            await mutation.mutateAsync({
                rfidCode,
                ...((label ?? '') !== (profile?.label ?? '') && { label: label?.trim() || null }),
                ...(dateOfBirth !== toDayString(profile?.dateOfBirth) && { dateOfBirth }),
                ...((sex ?? null) !== (profile?.sex ?? null) && { sex })
            });
            dismiss(label, 'confirm');
        }, tModals('rfid_profile.profile_updated'));
    };

    const pickPhoto = () => fileInputRef.current?.click();

    const onAvatarClick = () => {
        if (!avatarUrl) {
            pickPhoto();
            return;
        }

        presentActionSheet({
            buttons: [
                { text: tModals('rfid_profile.change_photo'), handler: pickPhoto },
                {
                    text: tModals('rfid_profile.remove_photo'),
                    role: 'destructive',
                    handler: () => {
                        presentResult(
                            () => avatarDeleteMutation.mutateAsync({ rfidCode }),
                            tModals('rfid_profile.photo_removed')
                        );
                    }
                },
                { text: tCommon('buttons.cancel'), role: 'cancel' }
            ]
        });
    };

    // Picked photo pending in the crop step. The crop modal is open while set.
    const [cropSource, setCropSource] = useState<{ url: string; image: HTMLImageElement } | null>(null);

    const closeCropper = () => {
        setCropSource((source) => {
            if (source) {
                URL.revokeObjectURL(source.url);
            }
            return null;
        });
    };

    const onFilePicked = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) {
            return;
        }

        const url = URL.createObjectURL(file);
        presentResult(async () => {
            try {
                setCropSource({ url, image: await loadImage(url) });
            } catch (error) {
                URL.revokeObjectURL(url);
                throw error;
            }
        });
    };

    const onCropConfirmed = (crop: CropRect) => {
        const source = cropSource;
        // Revoking the URL is safe here: the crop renders from the already
        // decoded image element, not from the blob URL.
        closeCropper();
        if (!source) {
            return;
        }

        presentResult(async () => {
            const image = await renderAvatarCrop(source.image, crop);
            await avatarUploadMutation.mutateAsync({ rfidCode, image });
        }, tModals('rfid_profile.photo_updated'));
    };

    const copyMicrochipCode = async () => {
        if (navigator.clipboard?.writeText) {
            presentResult(
                () => navigator.clipboard.writeText(rfidCode),
                tModals('rfid_profile.microchip_copied')
            );
        }
    };

    const dobModalRef = useRef<HTMLIonModalElement>(null);
    const displayName = label?.trim();

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton color="medium" onClick={() => dismiss(null, 'cancel')}>
                            {tCommon('buttons.cancel')}
                        </IonButton>
                    </IonButtons>
                    <IonTitle>{tModals('rfid_profile.title')}</IonTitle>
                    <IonButtons slot="end">
                        <IonButton onClick={save} strong={true} disabled={!canSave}>
                            {tCommon('buttons.save')}
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent ref={contentRef} fullscreen color="light">
                <div className={styles.hero}>
                    <button type="button" className={styles.avatar} onClick={onAvatarClick} aria-label={tModals('rfid_profile.add_photo')}>
                        {avatarUrl
                            ? <img src={avatarUrl} alt="" className={styles.avatarImage} />
                            : (
                                <span className={styles.avatarPlaceholder}>
                                    <IonIcon src="/assets/activity/activity-icon.svg" className={styles.avatarPlaceholderMark} aria-hidden />
                                    <span className={styles.avatarPlaceholderLabel}>{tModals('rfid_profile.add_photo')}</span>
                                </span>
                            )}
                        <span className={styles.avatarBadge}><IonIcon icon={cameraOutline} /></span>
                    </button>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={onFilePicked} hidden />

                    <div className={styles.nameRow}>
                        <span ref={nameMirrorRef} className={styles.nameMirror} aria-hidden>
                            {label || tComponents('rfid_profile.name_placeholder')}
                        </span>
                        <input
                            ref={nameInputRef}
                            className={styles.nameInput}
                            style={{ width: Math.min(280, Math.max(64, nameWidth + 4)) }}
                            value={label ?? ''}
                            maxLength={32}
                            placeholder={tComponents('rfid_profile.name_placeholder')}
                            aria-label={tComponents('rfid_profile.name')}
                            onChange={(event) => setLabel(event.target.value || undefined)}
                        />
                        <button type="button" className={styles.namePencil} aria-hidden tabIndex={-1} onClick={() => nameInputRef.current?.focus()}>
                            <IonIcon icon={createOutline} />
                        </button>
                    </div>

                    {lastSeenStatus && (
                        <div className={styles.lastSeen}>
                            {lastSeenStatus}
                            {' · '}
                            <Timestamp timestamp={lastSeen?.locationUpdatedAt ?? lastSeen?.eventTimestamp} short />
                        </div>
                    )}

                    {lastSeen?.location && (
                        <span className={`${styles.locationPill} ${lastSeen.location === 'INSIDE' ? styles.locationPillInside : styles.locationPillOutside}`}>
                            <span className={styles.locationDot} />
                            {tModals(`rfid_profile.${lastSeen.location === 'INSIDE' ? 'inside' : 'outside'}`)}
                        </span>
                    )}
                </div>

                <div className={statStyles.body}>
                    <StatsSection title={tComponents('stats.last_30_days')}>
                        {statsQuery.isLoading && (fleetWideStats || deviceIds.length > 0) && (
                            <>
                                <IonSkeletonText animated className={statStyles.cardSkeleton} />
                                <IonSkeletonText animated className={statStyles.cardSkeleton} />
                            </>
                        )}

                        {stats && !stats.hasActivity && (
                            <div className={`${statStyles.card} ${statStyles.emptyCard}`}>{tComponents('stats.no_activity')}</div>
                        )}

                        {stats && stats.hasActivity && (
                            <>
                                <PreyCard
                                    preyAttempts={stats.preyAttempts}
                                    lastPreyDay={stats.lastPreyDay}
                                    daysSinceLastPrey={stats.daysSinceLastPrey}
                                />
                                <TransitsCard
                                    transits={stats.transits}
                                    transitsIn={stats.transitsIn}
                                    transitsOut={stats.transitsOut}
                                    trendPct={stats.transitsTrendPct}
                                />
                                <DenyPeekTiles denies={stats.denies} peeks={stats.peeks} />
                            </>
                        )}
                    </StatsSection>

                    {stats && stats.hasActivity && stats.transits > 0 && (
                        <>
                            <StatsSection title={displayName
                                ? tModals('rfid_profile.flap_usage', { name: displayName })
                                : tModals('rfid_profile.flap_usage_unnamed')}>
                                <HourHistogramCard
                                    hourHistogram={stats.hourHistogram}
                                    busiestHour={stats.busiestHour}
                                    coreHours={stats.coreHours}
                                />
                            </StatsSection>

                            <StatsSection title={tComponents('stats.transits_by_weekday')}>
                                <WeekdayCard
                                    weekdayCounts={stats.weekdayCounts}
                                    busiestWeekday={stats.busiestWeekday}
                                    avgTransitsPerDay={stats.avgTransitsPerDay}
                                />
                            </StatsSection>

                            {monthly.length >= 2 && (
                                <StatsSection title={tComponents('stats.transits_by_month')}>
                                    <MonthlyTransitsCard monthly={monthly} />
                                </StatsSection>
                            )}

                            <StatsSection title={tComponents('stats.records')}>
                                <RecordsList
                                    mostActiveDay={stats.mostActiveDay}
                                    daysSinceLastPrey={stats.daysSinceLastPrey}
                                    lookbackDays={STATS_WINDOW_DAYS * 2}
                                />
                            </StatsSection>
                        </>
                    )}

                    <StatsSection title={tModals('rfid_profile.details')}>
                        <IonList inset={false} className={statStyles.listCard}>
                            <IonItem button detail id="rfid-profile-dob-row">
                                <IonLabel>
                                    <p>{tModals('rfid_profile.date_of_birth')}</p>
                                    <h3 className={dateOfBirth ? undefined : styles.placeholderValue}>
                                        {dateOfBirth
                                            ? formatDay(dateOfBirth, { dateStyle: 'long' })
                                            : tModals('rfid_profile.add_date_of_birth')}
                                    </h3>
                                </IonLabel>
                            </IonItem>
                            <IonItem>
                                <IonSelect
                                    label={tModals('rfid_profile.sex')}
                                    labelPlacement="stacked"
                                    interface="action-sheet"
                                    placeholder={tModals('rfid_profile.add_sex')}
                                    value={sex ?? undefined}
                                    onIonChange={(event) => setSex((event.detail.value || null) as RfidProfileSex | null)}
                                >
                                    {RFID_PROFILE_SEX_VALUES.map((value) => (
                                        <IonSelectOption key={value} value={value}>
                                            {tModals(`rfid_profile.sex_${value.toLowerCase()}`)}
                                        </IonSelectOption>
                                    ))}
                                    {sex && <IonSelectOption value="">{tModals('rfid_profile.sex_unspecified')}</IonSelectOption>}
                                </IonSelect>
                            </IonItem>
                            <IonItem lines="none">
                                <IonLabel>
                                    <p>{tComponents('rfid_profile.microchip_code')}</p>
                                    <h3 className={styles.microchipCode}>{rfidCode}</h3>
                                </IonLabel>
                                <IonButton slot="end" fill="clear" color="medium" onClick={copyMicrochipCode} aria-label={tModals('rfid_profile.copy_microchip_code')}>
                                    <IonIcon slot="icon-only" icon={copyOutline} />
                                </IonButton>
                            </IonItem>
                        </IonList>
                    </StatsSection>
                </div>

                <IonModal isOpen={!!cropSource} onDidDismiss={closeCropper} className={styles.cropModal}>
                    {cropSource && (
                        <AvatarCropper
                            key={cropSource.url}
                            url={cropSource.url}
                            width={cropSource.image.naturalWidth}
                            height={cropSource.image.naturalHeight}
                            onCancel={closeCropper}
                            onConfirm={onCropConfirmed}
                        />
                    )}
                </IonModal>

                <IonModal ref={dobModalRef} trigger="rfid-profile-dob-row" className={styles.datetimeModal}>
                    <IonDatetime
                        presentation="date"
                        preferWheel
                        value={dateOfBirth ?? undefined}
                        max={toDay}
                        showDefaultButtons
                        showClearButton
                        onIonChange={(event) => {
                            const value = event.detail.value;
                            setDateOfBirth(typeof value === 'string' ? value.slice(0, 10) : null);
                            dobModalRef.current?.dismiss();
                        }}
                        onIonCancel={() => dobModalRef.current?.dismiss()}
                    />
                </IonModal>
            </IonContent>
        </IonPage>
    );
};

export default RfidProfileModal;
